import { useEffect, useState } from "react";
import { Loader2, Lock, ShieldAlert, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { authorizeUser, useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { TypeUser } from "@/types/userType";
import { PERMISSIONS } from "@/constants/permissions";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { getSelectTenants } from "@/Api/tenants";
import {
  ImpersonationFilters,
  ImpersonationUserRow,
  getUsersForImpersonation,
  impersonateUser,
} from "@/Api/impersonation";
import { ImpersonationFiltersBar, TenantOption } from "@/App/Impersonation/Filters";
import { ImpersonationTable } from "@/App/Impersonation/Table";

/**
 * Tela de acesso como usuário (personificação).
 *
 * Acesso liberado para:
 * - Super Admin (user_type_id = 1): todos os tenants;
 * - Admin do Tenant (user_type_id = 2) com a permissão "impersonate-users":
 *   apenas usuários do próprio tenant.
 *
 * A verificação aqui é só de interface. O backend bloqueia os endpoints pelo
 * middleware "panel.permission" e recusa alvos administradores.
 */
export const PageList = () => {
  const { user } = useAuth();
  const { user: currentUser, hasPermission } = usePermissions();

  const isSuperAdmin = user?.user_type_id === TypeUser.admin;
  const isTenantUser = user?.user_type_id === TypeUser.client;
  const isTenantAdmin = isTenantUser && hasPermission(PERMISSIONS.IMPERSONATE_USERS);

  /** Permissões chegam pelo /me; enquanto não chegam não é possível decidir. */
  const isCheckingPermissions = !isSuperAdmin && isTenantUser && !currentUser;

  const canAccess = isSuperAdmin || isTenantAdmin;

  const [usersData, setUsersData] = useState({} as ApiDataProps<ImpersonationUserRow>);
  const [filters, setFilters] = useState<ImpersonationFilters>({});
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [state, setState] = useState("loading");
  const [impersonatingUuid, setImpersonatingUuid] = useState<string | null>(null);

  const loadUsers = async (page = 1, appliedFilters: ImpersonationFilters = filters) => {
    setState("loading");

    try {
      const { data, ok } = await getUsersForImpersonation(page, appliedFilters);

      if (ok) setUsersData(data?.response);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    } finally {
      setState("production");
    }
  };

  useEffect(() => {
    if (!canAccess) return;

    loadUsers(1, {});
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess || !isSuperAdmin) return;

    (async () => {
      const { data, ok } = await getSelectTenants();

      if (ok) setTenants(data?.response ?? []);
    })();
  }, [canAccess, isSuperAdmin]);

  const handleSearch = (newFilters: ImpersonationFilters) => {
    setFilters(newFilters);
    loadUsers(1, newFilters);
  };

  const pageActions: TypePageActions = {
    next: () => {
      if (usersData?.current_page < usersData?.last_page) {
        loadUsers(usersData.current_page + 1);
      }
    },
    previous: () => {
      if (usersData?.current_page > 1) {
        loadUsers(usersData.current_page - 1);
      }
    },
    lastPage: () => {
      if (usersData?.current_page !== usersData?.last_page) {
        loadUsers(usersData.last_page);
      }
    },
  };

  const handleImpersonate = async (target: ImpersonationUserRow) => {
    setImpersonatingUuid(target.uuid);

    try {
      const { data, ok } = await impersonateUser(target.uuid);

      if (!ok || !data?.response?.token) {
        toast.error(data?.message || "Não foi possível acessar como este usuário");
        return;
      }

      const { token, user: impersonatedUser, impersonated_by, expires_at } = data.response;

      authorizeUser({
        user: impersonatedUser,
        token,
        impersonation: {
          by: impersonated_by,
          expires_at: expires_at ?? null,
        },
      });

      window.location.href = "/dashboard";
    } catch (error) {
      console.error("Erro ao acessar como usuário:", error);
      toast.error("Não foi possível acessar como este usuário");
    } finally {
      setImpersonatingUuid(null);
    }
  };

  if (isCheckingPermissions) {
    return (
      <Main>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Main>
    );
  }

  if (!canAccess) {
    return (
      <Main>
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <Lock className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Acesso restrito</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            O acesso como usuário está disponível para o Super Admin e para
            administradores com a permissão de acesso como usuário.
          </p>
        </div>
      </Main>
    );
  }

  return (
    <Main>
      <PageHeader
        icon={UserCog}
        title="Acesso como usuário"
        description="Entre no sistema com a conta de um usuário para dar suporte ou conferir o que ele vê"
      />

      <div className="mb-4 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          O acesso dura 3 horas e fica registrado no log (início e fim, com o seu
          usuário). Enquanto estiver acessando como outra pessoa, tudo o que for feito
          é gravado no nome dela. Administradores não podem ser acessados.
          {!isSuperAdmin && " Você enxerga apenas os usuários da sua empresa."}
        </span>
      </div>

      <div className="flex flex-col gap-4">
        <ImpersonationFiltersBar
          isSuperAdmin={isSuperAdmin}
          tenants={tenants}
          loading={state === "loading"}
          onSearch={handleSearch}
        />

        {state === "loading" ? (
          <CreateLoaderTable cols={isSuperAdmin ? 6 : 5} lines={10} />
        ) : (
          <ImpersonationTable
            users={usersData?.data || []}
            dataApi={usersData}
            pageActions={pageActions}
            isSuperAdmin={isSuperAdmin}
            impersonatingUuid={impersonatingUuid}
            onImpersonate={handleImpersonate}
          />
        )}
      </div>
    </Main>
  );
};
